import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Play, Calendar, Zap, Skull, Infinity as InfinityIcon, ChevronDown, ChevronUp, Flame } from "lucide-react";
import Navbar from "../components/Navbar";
import LeaderboardTabs from "../components/LeaderboardTabs";
import SkinSelector from "../components/SkinSelector";
import PushToggle from "../components/PushToggle";
import NextTrophyCard from "../components/NextTrophyCard";
import DailyLoginModal from "../components/DailyLoginModal";
import LiveTournamentChip from "../components/LiveTournamentChip";
import api from "../lib/api";
import { SECTORS, SECTOR_SIZE, TOTAL_LEVELS, getSector, getSectorShapes } from "../lib/levels";
import { useAuth } from "../context/AuthContext";

const SLAYERS_COLLAPSED_KEY = "cosmic_slayers_collapsed";

export default function LevelSelect() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [bossesDefeated, setBossesDefeated] = useState(0);
  // Surfaces the pilot's current daily streak in the Daily Run CTA
  // below as a loss-aversion prompt ("🔥 5-day streak — don't break it!").
  // Streak data lives in /api/achievements `stats.daily_streak`, same
  // source as the Daily page widget.
  const [dailyStreak, setDailyStreak] = useState(0);
  // Collapsible Your Slayers widget. Only visible as a toggle on mobile
  // (sm:hidden on the chevron) — desktop has plenty of vertical room and
  // always shows the full panel. Persisted across sessions via
  // localStorage so pilots who don't care about boss-rank stats don't
  // re-collapse on every visit.
  const [slayersCollapsed, setSlayersCollapsed] = useState(() => {
    try { return localStorage.getItem(SLAYERS_COLLAPSED_KEY) === "1"; }
    catch { return false; }
  });
  const toggleSlayers = () => {
    setSlayersCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(SLAYERS_COLLAPSED_KEY, next ? "1" : "0"); }
      catch { /* noop */ }
      return next;
    });
  };

  useEffect(() => {
    api
      .get("/progress")
      .then((r) => setProgress(r.data))
      .catch(() => setProgress({ unlocked_level: 1, high_scores: {}, total_score: 0 }));
    // Pull the sector-boss count for the new chip — sector_bosses_defeated
    // is incremented by /progress/complete on every unique boss kill.
    // Same call also surfaces the daily_streak number used in the Daily
    // Run CTA loss-aversion chip below.
    api.get("/achievements")
      .then((r) => {
        const stats = r.data?.stats || {};
        setBossesDefeated(Number(stats.sector_bosses_defeated || 0));
        setDailyStreak(Number(stats.daily_streak || 0));
      })
      .catch(() => {});
  }, []);

  const unlocked = progress?.unlocked_level ?? 1;
  const totalScore = progress?.total_score ?? 0;
  // Personal Boss-Rank tally — counts S/A/B/C across `progress.boss_ranks`
  // and computes coverage vs the 294 total Leviathan boss levels (every
  // 10 from 10 to 3000 minus the 6 sector bosses at /500). Drives the
  // "Your Slayers" widget below the Next Trophy card.
  const bossRanks = progress?.boss_ranks || {};
  const bossRankCounts = { S: 0, A: 0, B: 0, C: 0 };
  for (const v of Object.values(bossRanks)) {
    if (v in bossRankCounts) bossRankCounts[v] += 1;
  }
  const bossRankTotal = bossRankCounts.S + bossRankCounts.A + bossRankCounts.B + bossRankCounts.C;
  const TOTAL_LEVIATHAN_BOSS_LEVELS = 294;

  // Tap-through targets for the three stat chips. Kept as derived values
  // so the links stay in sync with progress without extra state.
  //   - NEXT LEVEL → sector that contains the first still-locked level.
  //   - BOSSES → sector containing the NEXT uncleared boss (every 500th
  //              level); if all 6 cleared, the final sector.
  //   - TOTAL → trophies page (total_score unlocks most trophies, so
  //             tapping through rewards the pilot's cumulative grind).
  const nextLevelSector = getSector(Math.min(unlocked, TOTAL_LEVELS));
  const nextBossLevel = Math.min(6, bossesDefeated + 1) * 500;
  const nextBossSector = getSector(nextBossLevel);

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <DailyLoginModal />
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-16 fade-in">
        <LiveTournamentChip />
        <header className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-10">
          <div className="min-w-0 flex-1">
            <div className="font-mono-cosmic text-[10px] sm:text-xs tracking-[0.35em] text-[#00ffff] uppercase mb-1.5 sm:mb-2 truncate">
              // SELECT SECTOR · {TOTAL_LEVELS.toLocaleString()} LEVELS
            </div>
            <h1 className="font-heading font-black uppercase text-2xl sm:text-4xl md:text-5xl tracking-tighter leading-none">
              Pilot Briefing
            </h1>
            {user && user !== false && (
              <p className="mt-2 text-white/60 text-xs sm:text-sm truncate">
                Welcome, <span className="text-[#00ffff]">{user.name || user.email}</span>
              </p>
            )}
          </div>
          <div className="glass p-3 sm:p-4 flex items-center justify-between sm:justify-start gap-3 sm:gap-6 flex-shrink-0 w-full sm:w-auto" data-testid="stats-panel">
            <Link
              to={`/sector/${nextLevelSector.id}#${Math.min(unlocked, TOTAL_LEVELS)}`}
              className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00ff41]/60"
              data-testid="stat-next-level-link"
              title={`Jump to ${nextLevelSector.name.toUpperCase()} — sector of level #${unlocked.toLocaleString()}`}
            >
              <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] text-white/50 group-hover:text-[#00ff41] uppercase transition">
                Next Level
              </div>
              <div className="font-heading font-black text-base sm:text-2xl text-[#00ff41] group-hover:underline underline-offset-4 decoration-[#00ff41]/50" data-testid="stat-unlocked">
                #{unlocked.toLocaleString()}
              </div>
            </Link>
            <div className="w-px h-7 sm:h-10 bg-white/10" />
            <Link
              to="/achievements"
              className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd700]/60"
              data-testid="stat-total-score-link"
              title="Open Trophies"
            >
              <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] text-white/50 group-hover:text-[#ffd700] uppercase transition">
                Total
              </div>
              <div
                className="font-heading font-black text-base sm:text-2xl text-[#ffd700] group-hover:underline underline-offset-4 decoration-[#ffd700]/50"
                data-testid="stat-total-score"
              >
                {totalScore.toLocaleString()}
              </div>
            </Link>
            {/* Boss progress chip — earns the Sector Reaper achievement at 6/6.
                Glows when the pilot has cleared at least one boss; full crimson
                at 6/6 (final boss = Void Apocalypse). Tap-through jumps to the
                sector containing the NEXT uncleared boss (or the final sector
                when all 6 are done). */}
            <div className="w-px h-7 sm:h-10 bg-white/10" />
            <Link
              to={`/sector/${nextBossSector.id}#${nextBossLevel}`}
              className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff007f]/60"
              data-testid="stat-bosses-link"
              title={
                bossesDefeated >= 6
                  ? "All 6 bosses cleared · review the final sector"
                  : `Jump to ${nextBossSector.name.toUpperCase()} — next boss at level #${nextBossLevel}`
              }
            >
              <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] text-white/50 group-hover:text-[#ff007f] uppercase flex items-center gap-1 transition">
                <Skull
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${
                    bossesDefeated >= 6 ? "text-[#ff007f]" : "text-white/50 group-hover:text-[#ff007f]"
                  } transition`}
                />
                Bosses
              </div>
              <div
                className="font-heading font-black text-base sm:text-2xl group-hover:underline underline-offset-4 decoration-current/50"
                data-testid="stat-bosses"
                style={{
                  color: bossesDefeated >= 6 ? "#ff007f" : bossesDefeated > 0 ? "#ffd700" : "#7d7d8a",
                  textShadow: bossesDefeated >= 6 ? "0 0 8px rgba(255,0,127,0.55)" : undefined,
                }}
              >
                {bossesDefeated}/6
              </div>
            </Link>
          </div>
        </header>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
          <div className="min-w-0">
            <NextTrophyCard />

            {/* Personal Boss-Rank Progress — surfaces the pilot's S/A/B/C
                tally + total coverage vs all 294 Leviathan boss levels.
                Long-term goal track that complements the leaderboard:
                pilots see their own slayer numbers without leaving the
                dashboard. Hidden until the pilot has logged at least one
                rank so the empty state isn't noisy on first run. */}
            {bossRankTotal > 0 && (() => {
              const pct = Math.min(100, (bossRankTotal / TOTAL_LEVIATHAN_BOSS_LEVELS) * 100);
              const sPct = Math.min(100, (bossRankCounts.S / TOTAL_LEVIATHAN_BOSS_LEVELS) * 100);
              return (
                <div
                  className="glass p-4 sm:p-5 mb-6 border border-[#ffd700]/35 hover:border-[#ffd700]/60 transition"
                  data-testid="boss-rank-progress-widget"
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.35em] text-[#ffd700] uppercase">
                      <Skull className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Your Slayers
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] text-white/40 uppercase tabular-nums">
                        {bossRankTotal}/{TOTAL_LEVIATHAN_BOSS_LEVELS} bosses
                      </div>
                      {/* Mobile-only collapse toggle. Desktop has the
                          space; mobile pilots who don't care about boss
                          stats can hide the count grid + bar to keep
                          the level grid above the fold. */}
                      <button
                        type="button"
                        onClick={toggleSlayers}
                        className="sm:hidden p-1 -mr-1 text-[#ffd700]/70 hover:text-[#ffd700] transition"
                        aria-label={slayersCollapsed ? "Show slayer details" : "Hide slayer details"}
                        aria-expanded={!slayersCollapsed}
                        data-testid="slayers-collapse-toggle"
                      >
                        {slayersCollapsed
                          ? <ChevronDown className="w-3.5 h-3.5" />
                          : <ChevronUp className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  {/* Always visible: just the rank-S count + total
                      progress bar when collapsed (mobile only). The
                      `sm:!block` override forces the full panel on
                      desktop regardless of state. */}
                  <div className={`${slayersCollapsed ? "hidden sm:block" : "block"}`}>
                  <div
                    className="grid grid-cols-4 gap-2 mb-4"
                    data-testid="boss-rank-counts"
                  >
                    {[
                      { rk: "S", color: "#ffd700", count: bossRankCounts.S },
                      { rk: "A", color: "#7cffb0", count: bossRankCounts.A },
                      { rk: "B", color: "#67e8f9", count: bossRankCounts.B },
                      { rk: "C", color: "#cbd5e1", count: bossRankCounts.C },
                    ].map(({ rk, color, count }) => (
                      <div
                        key={rk}
                        className="border bg-black/40 px-2 py-1.5 flex items-baseline justify-between gap-1"
                        style={{ borderColor: `${color}55`, boxShadow: `0 0 6px ${color}25` }}
                        data-testid={`boss-rank-count-${rk}`}
                      >
                        <span
                          className="font-heading text-base sm:text-lg leading-none"
                          style={{ color, textShadow: `0 0 8px ${color}` }}
                        >
                          {rk}
                        </span>
                        <span className="font-mono-cosmic text-[10px] sm:text-xs tabular-nums text-white">
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="relative h-2 border border-white/10 bg-black/60 overflow-hidden">
                    {/* Outer bar = total coverage (white/dim), inner = S-rank coverage (gold). */}
                    <div
                      className="absolute inset-y-0 left-0 bg-white/40"
                      style={{ width: `${pct}%` }}
                    />
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{
                        width: `${sPct}%`,
                        background: "linear-gradient(90deg, #ffd700, #ff8eff)",
                        boxShadow: "0 0 8px rgba(255,215,0,0.6)",
                      }}
                      data-testid="boss-rank-progress-bar-s"
                    />
                  </div>
                  <div className="mt-2 font-mono-cosmic text-[8px] sm:text-[9px] tracking-[0.3em] text-white/40 uppercase flex items-center justify-between gap-2">
                    <span>· {pct.toFixed(1)}% slain · {sPct.toFixed(1)}% S-ranked ·</span>
                    <span className="text-[#ffd700]/70">all-S · 100%</span>
                  </div>
                  </div>
                  {/* Compact-collapsed summary — visible only when collapsed
                      on mobile. Shows S-count + slain%, the two most
                      gameplay-meaningful numbers, so pilots can still
                      glance at progress without expanding. */}
                  {slayersCollapsed && (
                    <div className="sm:hidden font-mono-cosmic text-[10px] tracking-[0.3em] text-white/55 uppercase tabular-nums flex items-center gap-3">
                      <span><span className="text-[#ffd700]">{bossRankCounts.S} S</span> · {pct.toFixed(0)}% slain</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Daily Run CTA */}
            <Link
              to="/daily"
              className="block glass p-4 sm:p-5 mb-6 border border-[#ff007f]/40 hover:border-[#ff007f] hover:shadow-[0_0_18px_rgba(255,0,127,0.25)] transition"
              data-testid="daily-run-cta"
            >
              <div className="flex items-center justify-between gap-3 sm:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.35em] text-[#ff007f] uppercase mb-1">
                    <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" /> Daily Cosmic Run
                  </div>
                  <div className="font-heading uppercase text-sm sm:text-lg tracking-tight text-white leading-snug">
                    Same seed · Resets 00:00 UTC
                  </div>
                  <p className="text-white/60 text-xs sm:text-sm mt-1 leading-snug">
                    Fresh deterministic level every day. Race the global board.
                  </p>
                  {/* Streak chip — only when the pilot has a streak going.
                      Loss-aversion prompt: clearer "don't break it" framing
                      at 3+ days; warm encouragement on shorter streaks. The
                      chip also bumps shield-aware copy on day 14+ where the
                      pilot has earned a shield (handled in /daily). */}
                  {dailyStreak > 0 && (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 border border-[#ff8c1a]/50 bg-[#ff8c1a]/10 text-[#ff8c1a] font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.25em] uppercase"
                      data-testid="daily-streak-chip"
                    >
                      <Flame className="w-3 h-3" />
                      <span className="tabular-nums">{dailyStreak}-day streak</span>
                      <span className="text-[#ff8c1a]/70 hidden sm:inline">·</span>
                      <span className="text-[#ff8c1a]/70 hidden sm:inline">
                        {dailyStreak >= 3 ? "don't break it!" : "keep going!"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="w-9 h-9 sm:w-10 sm:h-10 border border-[#ff007f] flex items-center justify-center text-[#ff007f] flex-shrink-0">
                  <Zap className="w-4 h-4" />
                </div>
              </div>
            </Link>

            {/* Endless Mode CTA */}
            <Link
              to="/endless"
              className="block glass p-4 sm:p-5 mb-6 border border-[#b026ff]/40 hover:border-[#b026ff] hover:shadow-[0_0_18px_rgba(176,38,255,0.3)] transition"
              data-testid="endless-cta"
            >
              <div className="flex items-center justify-between gap-3 sm:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.35em] text-[#b026ff] uppercase mb-1">
                    <InfinityIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" /> Endless Mode
                  </div>
                  <div className="font-heading uppercase text-sm sm:text-lg tracking-tight text-white leading-snug">
                    Wave-based survival · 4 trophies on the line
                  </div>
                  <p className="text-white/60 text-xs sm:text-sm mt-1 leading-snug">
                    Each cleared wave spawns a tougher one. Score, ball speed,
                    and tier-4 density ramp through all 6 sectors.
                  </p>
                </div>
                <div className="w-9 h-9 sm:w-10 sm:h-10 border border-[#b026ff] flex items-center justify-center text-[#b026ff] flex-shrink-0">
                  <InfinityIcon className="w-4 h-4" />
                </div>
              </div>
            </Link>

            <div className="grid md:grid-cols-2 gap-5" data-testid="sector-grid">
              {SECTORS.map((s, i) => {
                const reached = unlocked >= s.from;
                const completed = unlocked > s.to;
                const done = Math.max(0, Math.min(SECTOR_SIZE, unlocked - s.from));
                const pct = Math.round((done / SECTOR_SIZE) * 100);
                const resumeLevel = reached ? Math.min(s.to, Math.max(s.from, unlocked)) : s.from;
                return (
                  <div
                    key={s.id}
                    className={`level-card p-4 sm:p-5 flex flex-col justify-between ${
                      reached ? "" : "locked"
                    }`}
                    style={{
                      borderColor: reached ? `${s.accent}55` : undefined,
                    }}
                    data-testid={`sector-card-${s.id}`}
                  >
                    <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono-cosmic text-[10px] sm:text-[11px] tracking-[0.3em] text-white/50 uppercase">
                          Sector {String(i + 1).padStart(2, "0")}
                        </div>
                        <div
                          className="font-heading font-black text-xl sm:text-3xl tracking-tighter mt-1 truncate"
                          style={{ color: reached ? s.accent : "#4a4a5a" }}
                        >
                          {s.name}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!reached ? (
                          <Lock className="w-4 h-4 text-white/40" />
                        ) : completed ? (
                          <span className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-widest uppercase text-[#00ff41]">
                            Cleared
                          </span>
                        ) : (
                          <span className="w-2 h-2 bg-[#00ff41] animate-pulse rounded-full" />
                        )}
                      </div>
                    </div>

                    <div className="flex-1 mb-4 min-w-0">
                      <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-widest uppercase text-white/50 flex items-center justify-between gap-2">
                        <span className="truncate">Levels #{s.from.toLocaleString()} – #{s.to.toLocaleString()}</span>
                        <span className="flex-shrink-0" style={{ color: s.accent }}>{pct}%</span>
                      </div>
                      <div className="mt-2 h-1.5 bg-white/5 overflow-hidden">
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${pct}%`,
                            background: `linear-gradient(90deg, ${s.accent}, #ffffff22)`,
                          }}
                        />
                      </div>
                      {/* Shapes-inside teaser — turns the sector picker
                          into a preview of what cosmic art is coming.
                          Locked sectors hide the list to preserve a
                          touch of mystery. */}
                      {reached && (
                        <div className="mt-3" data-testid={`sector-shapes-${s.id}`}>
                          <div className="font-mono-cosmic text-[8px] sm:text-[9px] tracking-[0.3em] uppercase text-white/40 mb-1.5">
                            Shapes inside
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {getSectorShapes(s.id).slice(0, 5).map((shape) => (
                              <span
                                key={shape.id}
                                className="px-1.5 py-0.5 border font-mono-cosmic text-[8px] sm:text-[9px] tracking-[0.15em] uppercase truncate"
                                style={{
                                  borderColor: shape.isSignature ? `${s.accent}99` : "rgba(255,255,255,0.15)",
                                  color: shape.isSignature ? s.accent : "rgba(255,255,255,0.7)",
                                  background: shape.isSignature ? `${s.accent}11` : "transparent",
                                }}
                                title={shape.isSignature ? `${shape.label} (signature — only here)` : shape.label}
                                data-testid={`sector-shape-${s.id}-${shape.id}`}
                              >
                                {shape.label}
                              </span>
                            ))}
                            {getSectorShapes(s.id).length > 5 && (
                              <span
                                className="px-1.5 py-0.5 font-mono-cosmic text-[8px] sm:text-[9px] tracking-[0.15em] uppercase text-white/35"
                                title={getSectorShapes(s.id).slice(5).map((sh) => sh.label).join(" · ")}
                              >
                                +{getSectorShapes(s.id).length - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="mt-3 font-mono-cosmic text-[9px] sm:text-[10px] tracking-widest uppercase text-white/40 truncate">
                        Reward · {s.skinName}
                      </div>
                    </div>

                    {reached ? (
                      <div className="flex items-center gap-2 min-w-0">
                        <Link
                          to={`/play/${resumeLevel}`}
                          className="flex-1 min-w-0 py-2 px-2 text-center border font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.25em] uppercase transition inline-flex items-center justify-center gap-1 truncate"
                          style={{
                            borderColor: s.accent,
                            color: s.accent,
                          }}
                          data-testid={`sector-continue-${s.id}`}
                        >
                          <span className="truncate">{completed ? "Replay" : "Continue"} #{resumeLevel.toLocaleString()}</span>
                          <Play className="w-3 h-3 flex-shrink-0" />
                        </Link>
                        <Link
                          to={`/sector/${s.id}`}
                          className="py-2 px-2 sm:px-3 text-center border border-white/20 text-white/70 hover:border-white/50 hover:text-white font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.25em] uppercase transition flex-shrink-0"
                          data-testid={`sector-browse-${s.id}`}
                        >
                          All
                        </Link>
                      </div>
                    ) : (
                      <div className="text-center py-2 font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.25em] text-white/40 uppercase">
                        Clear level #{s.from - 1} to unlock
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-6 min-w-0">
            <LeaderboardTabs />
            <PushToggle />
            <SkinSelector />
          </div>
        </div>
      </main>
    </div>
  );
}
